import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import Mansory from 'react-masonry-component';

import { InfiniteScrollComponent, TextHighlighted } from '@playbookux/playbook-web-core';
import CardHighlight from '../card-highlight';
import SearchResultCard from '../search-result-card';

import extractTranscriptText from '../../utils/extract-transcript-text';
import { formatEntityType } from '../../utils/format-entity-type';
import typeEntity from '../../constants/entity-type';

import { MansoryContainer, MansoryGutter } from './styles';

const mansoryOptions = {
  columnWidth: '.mansory-sizer',
  horizontalOrder: true,
  itemSelector: '.mansory-item',
  gutter: '.mansory-gutter-sizer',
  percentPosition: true,
};

const CardsHighlights = (props) => {
  const {
    hasMore,
    highlights,
    onLoadMore,
    searchedText,
    selectedTagId,
    typeHighlight,
  } = props;

  const isFiltered = !_.isEmpty(selectedTagId);

  const handleClick = (entityId, entityType, url) => {
    if (isFiltered && entityType === 'HighlightReel') {
      window.open(`highlight-reel/${entityId}`, '_blank');
      return;
    }

    if (entityType === typeEntity.REPORT) {
      window.open(url);
      return;
    }

    window.open(url, '_blank');
  };

  const setHighlightsByEntityId = (highlightId, entityId) => (
    _.chain(highlights)
      .filter(({ id, entity }) => id === highlightId && entity.id === entityId)
      .uniqBy(['entity.id', 'tag.id'])
      .value()
  );

  return (
    <InfiniteScrollComponent
      useWindow
      hasMore={hasMore}
      onLoadMore={onLoadMore}
    >
      <Mansory options={mansoryOptions} enableResizableChildren>
        <MansoryContainer className="mansory-sizer" />
        <MansoryGutter className="mansory-gutter-sizer" />

        {typeHighlight === 'highlight' && highlights && _.map(highlights, (highlight) => {
          const {
            id,
            tag,
            project,
            createdAt,
            entityType,
            entity,
          } = highlight || {};

          if (!entity) {
            return null;
          }

          const {
            id: entityId,
            noteType,
            note,
            transcriptText,
            transcript,
            text,
            videoStartTime,
            testSession: { tester } = {},
          } = entity;

          const { id: testerId, user } = tester;

          const content = note || transcriptText || text || extractTranscriptText(transcript);

          const { tagGroup, name: tagName, tagType } = tag || {};
          const { color } = tagGroup || {};
          const tagHighlightsByEntity = setHighlightsByEntityId(id, entityId);
          const url = `projects/${project.id}/videos/${testerId}?time=${videoStartTime}`;
          const cardType = formatEntityType(entityType, noteType);

          return (
            <MansoryContainer
              key={`${id}-${tag.id}`}
              className="mansory-item"
            >
              <CardHighlight
                color={color}
                createdAt={createdAt}
                userName={user.profile.firstName}
                isFiltered={isFiltered}
                cardType={cardType}
                tagName={tagName}
                tagType={tagType}
                onClick={() => handleClick(entityId, entityType, url)}
                projectName={`${project.name} xxx`}
              >
                {isFiltered ? (
                  <TextHighlighted
                    tagHighlights={tagHighlightsByEntity}
                    text={content || ''}
                    searchedText={searchedText}
                  />
                ) : content}
              </CardHighlight>
            </MansoryContainer>
          );
        })}

        {typeHighlight === 'search' && highlights && highlights.map((highlight) => (
          <MansoryContainer
            key={`${highlight.id}-${highlight.searchableType}`}
            className="mansory-item"
          >
            <SearchResultCard
              highlight={highlight}
              onClick={handleClick}
              text={searchedText}
            />
          </MansoryContainer>
        ))}
      </Mansory>
    </InfiniteScrollComponent>
  );
};

CardsHighlights.propTypes = {
  highlights: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    tag: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      tagGroup: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        color: PropTypes.string,
      }),
    }),
    project: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    searchableType: PropTypes.string,
    createdAt: PropTypes.string,
    createdBy: PropTypes.shape({
      id: PropTypes.string,
      profile: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      }),
    }),
    searchable: PropTypes.shape({
      id: PropTypes.string,
    }),
    entity: PropTypes.shape({
      id: PropTypes.string,
      note: PropTypes.string,
      transcriptText: PropTypes.string,
      testSession: PropTypes.shape({
        tester: PropTypes.shape({
          id: PropTypes.string,
        }),
      }),
    }),
    charStart: PropTypes.number,
    charEnd: PropTypes.number,
  })).isRequired,
  selectedTagId: PropTypes.string,
  onLoadMore: PropTypes.func.isRequired,
  hasMore: PropTypes.bool.isRequired,
  typeHighlight: PropTypes.string,
  searchedText: PropTypes.string,
};

CardsHighlights.defaultProps = {
  typeHighlight: null,
  selectedTagId: null,
  searchedText: '',
};

export default CardsHighlights;
